import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Quote,
  Check,
  X as XIcon,
  MessageCircle,
  Send,
  Sprout,
  Building2,
  Languages,
  ShieldCheck,
  ArrowRight,
  Phone,
} from "lucide-react";
import {
  STATS,
  WHY_REASONS,
  WHY_IMGS,
  TESTIMONIALS,
  WORKER_FORM,
  WHATSAPP_PHONE,
} from "@/i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ----------------- useInView hook ----------------- */
export const useInView = (options = { threshold: 0.15 }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.unobserve(el);
        }
      },
      options,
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [options]);
  return [ref, inView];
};

export const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out`}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(24px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

/* ----------------- Animated counter ----------------- */
const AnimatedNumber = ({ value, duration = 1600 }) => {
  const [ref, inView] = useInView();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.floor(eased * value));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);
  return <span ref={ref}>{n}</span>;
};

export const Stats = ({ lang }) => (
  <section
    className="bg-gradient-to-b from-black to-surface border-b border-line py-14"
    data-testid="stats-section"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
      {STATS.map((s, i) => (
        <Reveal key={i} delay={i * 100} className="text-center">
          <div className="font-display text-5xl lg:text-6xl text-lime-400 font-bold tracking-tight">
            <AnimatedNumber value={s.value} />
            {s.suffix}
          </div>
          <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-white/65 font-medium">
            {lang === "es" ? s.label_es : s.label_en}
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ----------------- Why Choose Us ----------------- */
export const WhyChooseUs = ({ lang, t }) => {
  const reasons = WHY_REASONS[lang];
  return (
    <section id="why" className="bg-surface py-20 lg:py-24" data-testid="why-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-lime-400 font-bold mb-3">
            {lang === "es" ? "Por Qué las Granjas Nos Eligen" : "Why Farms Choose AGNM"}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white uppercase font-bold tracking-tight">
            {lang === "es" ? "Lo que hacemos diferente" : "What we do differently"}
          </h2>
          <div className="h-0.5 w-20 bg-lime-500 mx-auto mt-4" />
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {reasons.map((r, i) => (
            <Reveal key={i} delay={i * 120}>
              <div
                className="group relative h-full bg-black border border-line rounded-md overflow-hidden hover:border-lime-500 transition"
                data-testid={`why-card-${i}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={WHY_IMGS[i]}
                    alt={r.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-7">
                  <div className="text-[11px] uppercase tracking-[0.25em] text-lime-400 font-bold mb-3">
                    0{i + 1}
                  </div>
                  <h3 className="font-display text-xl uppercase tracking-[0.08em] text-white font-bold leading-tight">
                    {r.title}
                  </h3>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed">{r.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ----------------- Free Consultation callout ----------------- */
export const FreeConsultation = ({ lang }) => {
  const t =
    lang === "es"
      ? {
          badge: "Consulta Gratis",
          title: "15 minutos con un dueño. Sin compromiso.",
          body:
            "Cuéntenos sobre su granja y le explicaremos exactamente cómo funciona el proceso H-2A, cuánto cuesta y qué tan rápido podemos tener trabajadores en su campo.",
          cta: "Agendar Llamada Gratis",
          call: "O llame ahora",
        }
      : {
          badge: "Free Consultation",
          title: "15 minutes with an owner. No obligation.",
          body:
            "Tell us about your farm and we'll walk you through exactly how the H-2A process works, what it costs, and how fast we can get workers on your field.",
          cta: "Schedule Your Free Call",
          call: "Or call now",
        };
  return (
    <section
      className="bg-gradient-to-r from-lime-500/10 via-lime-500/5 to-transparent border-y border-lime-500/30 py-12 sm:py-14 lg:py-16"
      data-testid="consultation-section"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-6 sm:gap-8 items-center">
        <div className="lg:col-span-8">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-lime-400 font-bold bg-lime-500/10 border border-lime-500/40 rounded-full px-3 py-1.5 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-lime-400 animate-pulse" />
            {t.badge}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white uppercase font-bold tracking-tight leading-tight">
            {t.title}
          </h2>
          <p className="mt-4 text-white/75 max-w-2xl leading-relaxed">{t.body}</p>
        </div>
        <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
          <a href="#contact" className="w-full lg:w-auto">
            <button
              className="w-full lg:w-auto bg-lime-500 hover:bg-lime-400 text-black font-bold uppercase tracking-[0.15em] px-7 h-12 rounded-md flex items-center justify-center gap-2 transition"
              data-testid="consultation-cta"
            >
              {t.cta}
              <ArrowRight className="h-4 w-4" />
            </button>
          </a>
          <a
            href="tel:+12694643114"
            className="text-white/70 hover:text-lime-400 text-sm flex items-center gap-2 transition"
            data-testid="consultation-call"
          >
            <Phone className="h-3.5 w-3.5" />
            {t.call}: 269-464-3114
          </a>
        </div>
      </div>
    </section>
  );
};

/* ----------------- Testimonials ----------------- */
export const Testimonials = ({ lang }) => {
  const items = TESTIMONIALS[lang];
  return (
    <section className="bg-surface py-20 lg:py-24" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-lime-400 font-bold mb-3">
            {lang === "es" ? "Testimonios" : "Testimonials"}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-white uppercase font-bold tracking-tight">
            {lang === "es" ? "Lo que dicen nuestros socios" : "What our partners say"}
          </h2>
          <div className="h-0.5 w-20 bg-lime-500 mx-auto mt-4" />
        </Reveal>
        <div className="grid md:grid-cols-3 gap-5">
          {items.map((item, i) => (
            <Reveal key={i} delay={i * 120}>
              <div
                className="h-full bg-black border border-line rounded-md p-7 hover:border-lime-500 transition"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="h-7 w-7 text-lime-400/50 mb-3" />
                <p className="text-white/85 leading-relaxed italic">&ldquo;{item.quote}&rdquo;</p>
                <div className="mt-5 pt-5 border-t border-line flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-lime-500/15 border border-lime-500/40 flex items-center justify-center text-lime-400 font-display font-bold">
                    {item.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{item.name}</div>
                    <div className="text-xs text-white/55">{item.role}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ----------------- Worker Application Form ----------------- */
export const WorkerApplication = ({ lang }) => {
  const wf = WORKER_FORM[lang];
  const initial = {
    full_name: "",
    email: "",
    phone: "",
    country: "",
    age: "",
    languages: "",
    years_experience: "",
    crop_experience: "",
    has_passport: "yes",
    prior_visa: "none",
    available_from: "",
    notes: "",
  };
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        age: form.age ? parseInt(form.age, 10) : null,
        years_experience: form.years_experience ? parseInt(form.years_experience, 10) : null,
      };
      // Strip empty optional strings -> undefined so backend uses defaults
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await axios.post(`${API}/worker-application`, payload);
      toast.success(wf.success);
      setForm(initial);
    } catch (err) {
      console.error(err);
      toast.error(wf.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="worker-application"
      className="bg-black py-20 lg:py-24 border-t border-line"
      data-testid="worker-application-section"
    >
      <div className="mx-auto max-w-5xl px-6 lg:px-10">
        <Reveal className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-lime-400 font-bold mb-3">
            {wf.kicker}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white uppercase font-bold tracking-tight">
            {wf.title}
          </h2>
          <p className="mt-5 text-white/65 max-w-2xl mx-auto">{wf.subtitle}</p>
          <div className="h-0.5 w-20 bg-lime-500 mx-auto mt-5" />
        </Reveal>
        <Reveal>
          <form
            onSubmit={onSubmit}
            className="bg-surface border border-line rounded-md p-7 lg:p-10"
            data-testid="worker-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={wf.fields.full_name} required>
                <Input
                  required
                  value={form.full_name}
                  onChange={onChange("full_name")}
                  className="bg-black border-line text-white"
                  data-testid="worker-input-name"
                />
              </Field>
              <Field label={wf.fields.email} required>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange("email")}
                  className="bg-black border-line text-white"
                  data-testid="worker-input-email"
                />
              </Field>
              <Field label={wf.fields.phone} required>
                <Input
                  required
                  value={form.phone}
                  onChange={onChange("phone")}
                  className="bg-black border-line text-white"
                  data-testid="worker-input-phone"
                />
              </Field>
              <Field label={wf.fields.country}>
                <Input
                  value={form.country}
                  onChange={onChange("country")}
                  className="bg-black border-line text-white"
                  data-testid="worker-input-country"
                />
              </Field>
              <Field label={wf.fields.age}>
                <Input
                  type="number"
                  min={16}
                  max={80}
                  value={form.age}
                  onChange={onChange("age")}
                  className="bg-black border-line text-white"
                  data-testid="worker-input-age"
                />
              </Field>
              <Field label={wf.fields.languages}>
                <Input
                  value={form.languages}
                  onChange={onChange("languages")}
                  placeholder={lang === "es" ? "Español, Inglés..." : "Spanish, English..."}
                  className="bg-black border-line text-white"
                  data-testid="worker-input-languages"
                />
              </Field>
              <Field label={wf.fields.years_experience}>
                <Input
                  type="number"
                  min={0}
                  max={70}
                  value={form.years_experience}
                  onChange={onChange("years_experience")}
                  className="bg-black border-line text-white"
                  data-testid="worker-input-years"
                />
              </Field>
              <Field label={wf.fields.available_from}>
                <Input
                  value={form.available_from}
                  onChange={onChange("available_from")}
                  placeholder={lang === "es" ? "Marzo 2026" : "March 2026"}
                  className="bg-black border-line text-white"
                  data-testid="worker-input-available"
                />
              </Field>
              <Field label={wf.fields.has_passport}>
                <Select
                  value={form.has_passport}
                  onValueChange={(v) => setForm({ ...form, has_passport: v })}
                >
                  <SelectTrigger className="bg-black border-line text-white" data-testid="worker-select-passport">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">{wf.options.passport.yes}</SelectItem>
                    <SelectItem value="no">{wf.options.passport.no}</SelectItem>
                    <SelectItem value="in_progress">{wf.options.passport.in_progress}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={wf.fields.prior_visa}>
                <Select
                  value={form.prior_visa}
                  onValueChange={(v) => setForm({ ...form, prior_visa: v })}
                >
                  <SelectTrigger className="bg-black border-line text-white" data-testid="worker-select-prior">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{wf.options.prior.none}</SelectItem>
                    <SelectItem value="H2A">{wf.options.prior.H2A}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={wf.fields.crop_experience} className="sm:col-span-2">
                <Textarea
                  rows={3}
                  value={form.crop_experience}
                  onChange={onChange("crop_experience")}
                  className="bg-black border-line text-white"
                  data-testid="worker-input-crops"
                />
              </Field>
              <Field label={wf.fields.notes} className="sm:col-span-2">
                <Textarea
                  rows={3}
                  value={form.notes}
                  onChange={onChange("notes")}
                  className="bg-black border-line text-white"
                  data-testid="worker-input-notes"
                />
              </Field>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="mt-6 bg-lime-500 hover:bg-lime-400 text-black font-bold uppercase tracking-[0.15em] px-7 h-12 rounded-md"
              data-testid="worker-submit-btn"
            >
              {submitting ? wf.submitting : wf.submit}
              {!submitting && <Send className="ml-2 h-4 w-4" />}
            </Button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const Field = ({ label, required, children, className = "" }) => (
  <div className={className}>
    <Label className="text-[11px] uppercase tracking-[0.2em] text-white/60">
      {label} {required && <span className="text-lime-400">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

/* ----------------- WhatsApp Floating Button ----------------- */
export const WhatsAppButton = ({ lang }) => (
  <a
    href={`https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(
      lang === "es"
        ? "Hola AGNM, estoy interesado en trabajar / contratar."
        : "Hi AGNM, I'm interested in work / hiring.",
    )}`}
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-[#25D366] hover:bg-[#1faa54] shadow-2xl shadow-black/40 flex items-center justify-center transition-transform hover:scale-110"
    aria-label="WhatsApp"
    data-testid="whatsapp-float"
  >
    <MessageCircle className="h-6 w-6 text-white" />
    <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-lime-400 animate-ping" />
    <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-lime-400" />
  </a>
);
